"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { Send, Trash2, RefreshCw, Bot } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

interface Message {
  id?: number;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
}

interface Agent {
  id: string;
  name: string;
  emoji: string;
  role_label?: string;
  description?: string;
  status?: string;
}

export default function ChatPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Cargar agentes
  useEffect(() => {
    fetch(`${API}/agents`).then(r => r.json()).then(data => {
      const list = data.filter((a: Agent) => a.id !== 'mia');
      setAgents(list);
      if (list.length > 0) setSelectedAgent(list[0]);
    }).catch(() => {});
  }, []);

  // Cargar historial al cambiar de agente
  useEffect(() => {
    if (!selectedAgent) return;
    setLoadingHistory(true);
    fetch(`${API}/agents/${selectedAgent.id}/messages?limit=60`)
      .then(r => r.json())
      .then(data => {
        setMessages(Array.isArray(data) ? data : []);
        setLoadingHistory(false);
      })
      .catch(() => { setMessages([]); setLoadingHistory(false); });
  }, [selectedAgent]);

  // Scroll al fondo
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading || !selectedAgent) return;
    const userContent = input.trim();
    setInput("");

    const userMsg: Message = { role: "user", content: userContent };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Guardar mensaje usuario
    await fetch(`${API}/agents/${selectedAgent.id}/messages`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: "user", content: userContent }),
    }).catch(() => {});

    try {
      // Todos los agentes usan el gateway de OpenClaw (sesiones reales con workspace y memoria)
      let reply: string;
      const agentModel = selectedAgent.id === "dashboard" ? "openclaw/dashboard" : `openclaw/${selectedAgent.id}`;
      const r = await fetch(`${API}/agents/leo/chat-gateway`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: agentModel,
          messages: [
            ...messages.slice(-14).map(m => ({ role: m.role, content: m.content })),
            { role: "user", content: userContent },
          ],
        }),
      });
      const data = await r.json();
      reply = data.reply || "...";

      const assistantMsg: Message = { role: "assistant", content: reply };
      setMessages(prev => [...prev, assistantMsg]);

      // Guardar respuesta
      await fetch(`${API}/agents/${selectedAgent.id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: "assistant", content: reply }),
      }).catch(() => {});
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Error al conectar. Inténtalo de nuevo." }]);
    }

    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const clearHistory = async () => {
    if (!selectedAgent || !confirm(`¿Borrar historial con ${selectedAgent.name}?`)) return;
    await fetch(`${API}/agents/${selectedAgent.id}/messages`, { method: "DELETE" });
    setMessages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const formatTime = (ts?: string) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (ts?: string) => {
    if (!ts) return "";
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Hoy";
    if (d.toDateString() === yesterday.toDateString()) return "Ayer";
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "long" });
  };

  // Agrupar mensajes por fecha
  const groupedMessages = messages.reduce((acc, msg, i) => {
    const date = formatDate(msg.created_at);
    const prevDate = i > 0 ? formatDate(messages[i - 1].created_at) : null;
    if (date && date !== prevDate) acc.push({ type: "date", label: date, key: `date-${i}` });
    acc.push({ type: "msg", msg, key: `msg-${msg.id || i}` });
    return acc;
  }, [] as any[]);

  return (
    
      <div style={{ display: "flex", height: "calc(100vh - 64px)", gap: "0", margin: "-32px -40px", overflow: "hidden" }}>

        {/* ── Sidebar agentes ── */}
        <div style={{
          width: "260px", flexShrink: 0,
          background: "var(--bg-card)", borderRight: "1px solid var(--border)",
          display: "flex", flexDirection: "column", overflowY: "auto",
        }}>
          <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid var(--border)" }}>
            <h2 style={{ fontSize: "15px", fontWeight: 700 }}>Chat con el equipo</h2>
            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>Habla con tus agentes IA</p>
          </div>
          <div style={{ padding: "8px", flex: 1 }}>
            {agents.map(agent => {
              const active = selectedAgent?.id === agent.id;
              return (
                <button key={agent.id} onClick={() => setSelectedAgent(agent)} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", borderRadius: "10px", border: "none", cursor: "pointer",
                  background: active ? "var(--accent)15" : "transparent",
                  borderLeft: active ? `3px solid var(--accent)` : "3px solid transparent",
                  transition: "all 120ms",
                  textAlign: "left",
                }}>
                  <div style={{
                    width: "38px", height: "38px", borderRadius: "50%",
                    background: active ? "var(--accent)20" : "var(--bg-muted)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "20px", flexShrink: 0,
                  }}>
                    {agent.emoji}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: active ? 600 : 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.name}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.role_label || agent.description?.slice(0, 30) || "Agente IA"}</div>
                  </div>
                  {/* Dot de estado */}
                  <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: agent.status === "running" ? "#f59e0b" : "#22c55e", flexShrink: 0, marginLeft: "auto" }} />
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Área de chat ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg)" }}>

          {/* Header */}
          {selectedAgent && (
            <div style={{
              padding: "14px 24px", borderBottom: "1px solid var(--border)",
              display: "flex", alignItems: "center", gap: "12px",
              background: "var(--bg-card)", flexShrink: 0,
            }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--bg-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
                {selectedAgent.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "15px", fontWeight: 700 }}>{selectedAgent.name}</div>
                <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{selectedAgent.role_label || "Agente IA"} · <span style={{ color: "#22c55e" }}>● Online</span></div>
              </div>
              <button onClick={clearHistory} title="Borrar historial" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "6px", borderRadius: "8px", display: "flex", alignItems: "center" }}
                onMouseEnter={e => (e.currentTarget.style.color = "#ef4444")}
                onMouseLeave={e => (e.currentTarget.style.color = "var(--text-muted)")}>
                <Trash2 size={15} />
              </button>
            </div>
          )}

          {/* Mensajes */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "4px" }}>
            {!selectedAgent && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "12px", color: "var(--text-muted)" }}>
                <Bot size={40} style={{ opacity: 0.2 }} />
                <p style={{ fontSize: "14px" }}>Selecciona un agente para empezar</p>
              </div>
            )}

            {loadingHistory && (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                <RefreshCw size={16} color="var(--text-muted)" className="animate-spin" />
              </div>
            )}

            {!loadingHistory && selectedAgent && messages.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "10px", color: "var(--text-muted)", paddingTop: "60px" }}>
                <div style={{ fontSize: "40px" }}>{selectedAgent.emoji}</div>
                <p style={{ fontSize: "15px", fontWeight: 600, color: "var(--text)" }}>Hola, soy {selectedAgent.name}</p>
                <p style={{ fontSize: "13px", textAlign: "center", maxWidth: "320px" }}>{selectedAgent.description || "¿En qué puedo ayudarte hoy?"}</p>
              </div>
            )}

            {groupedMessages.map(item => {
              if (item.type === "date") {
                return (
                  <div key={item.key} style={{ display: "flex", alignItems: "center", gap: "10px", margin: "12px 0 8px" }}>
                    <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                    <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 500, whiteSpace: "nowrap" }}>{item.label}</span>
                    <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                  </div>
                );
              }
              const { msg } = item;
              const isUser = msg.role === "user";
              return (
                <div key={item.key} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: "2px" }}>
                  {!isUser && (
                    <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "var(--bg-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0, marginRight: "8px", alignSelf: "flex-end", marginBottom: "2px" }}>
                      {selectedAgent?.emoji}
                    </div>
                  )}
                  <div style={{ maxWidth: "68%" }}>
                    <div style={{
                      padding: "10px 14px",
                      borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      background: isUser ? "var(--bg-dark)" : "var(--bg-card)",
                      border: isUser ? "none" : "1px solid var(--border)",
                      color: isUser ? "white" : "var(--text)",
                      fontSize: "14px", lineHeight: 1.55,
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}>
                      {msg.content}
                    </div>
                    {msg.created_at && (
                      <div style={{ fontSize: "10px", color: "var(--text-light)", marginTop: "3px", textAlign: isUser ? "right" : "left", paddingInline: "4px" }}>
                        {formatTime(msg.created_at)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "var(--bg-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>
                  {selectedAgent?.emoji}
                </div>
                <div style={{ padding: "12px 16px", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "18px 18px 18px 4px" }}>
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--accent)", opacity: 0.7, animation: `bounce 1.2s ${i * 0.2}s infinite ease-in-out` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          {selectedAgent && (
            <div style={{ padding: "12px 24px 20px", borderTop: "1px solid var(--border)", background: "var(--bg-card)", flexShrink: 0 }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "14px", padding: "8px 12px", transition: "border-color 0.15s" }}
                onFocus={e => (e.currentTarget.style.borderColor = "var(--accent)")}
                onBlur={e => (e.currentTarget.style.borderColor = "var(--border)")}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Escribe a ${selectedAgent.name}... (Enter para enviar, Shift+Enter nueva línea)`}
                  disabled={loading}
                  rows={1}
                  style={{
                    flex: 1, background: "none", border: "none", outline: "none",
                    fontSize: "14px", color: "var(--text)", resize: "none",
                    fontFamily: "inherit", lineHeight: 1.5, maxHeight: "120px",
                    overflowY: "auto",
                  }}
                  onInput={e => {
                    const t = e.currentTarget;
                    t.style.height = "auto";
                    t.style.height = Math.min(t.scrollHeight, 120) + "px";
                  }}
                />
                <button onClick={send} disabled={loading || !input.trim()}
                  style={{
                    width: "36px", height: "36px", borderRadius: "10px",
                    background: input.trim() && !loading ? "var(--bg-dark)" : "var(--bg-muted)",
                    border: "none", cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    transition: "background 0.15s",
                  }}>
                  <Send size={15} color={input.trim() && !loading ? "white" : "var(--text-muted)"} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    
  );
}
